import React, { useState, useRef } from 'react';
import { Phone, Mail, Home } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { HCAPTCHA_SITE_KEY } from "@/lib/hcaptcha";

const ContactSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!captchaToken) {
      toast({
        title: "Vérification requise",
        description: "Veuillez compléter la vérification captcha.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    const contactData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      message: formData.get('message') as string,
      captchaToken,
    };

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: contactData,
      });

      if (error) throw error;

      toast({
        title: "Message envoyé !",
        description: "Nous vous contacterons bientôt. Un email de confirmation vous a été envoyé.",
      });
      
      // Reset form and captcha
      e.currentTarget.reset();
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } catch (error: any) {
      console.error('Error sending contact email:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur s'est produite lors de l'envoi. Veuillez réessayer.",
        variant: "destructive",
      });
      // Reset captcha on error
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-semibold mb-8 text-center">Contact</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-xl font-semibold mb-4">Nous contacter</h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-[#86AA8E]" />
              <span>06 50 42 90 73</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#86AA8E]" />
              <span>contact@ty-gwennili.com</span>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-xl font-semibold mb-4">Besoin de plus d'informations ?</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="name">
                Nom
              </label>
              <Input 
                id="name" 
                name="name" 
                placeholder="Votre nom" 
                required 
                minLength={2}
                maxLength={100}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="email">
                Email
              </label>
              <Input 
                id="email" 
                name="email" 
                type="email" 
                placeholder="Votre email" 
                required 
                maxLength={255}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="message">
                Message
              </label>
              <Textarea
                id="message"
                name="message"
                placeholder="Votre message"
                rows={4}
                required
                minLength={10}
                maxLength={2000}
              />
            </div>
            
            <div className="flex justify-center">
              <HCaptcha
                ref={captchaRef}
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={isSubmitting || !captchaToken} 
              className="w-full bg-[#86AA8E] hover:bg-opacity-90"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
